import { ResultOf } from "@graphql-typed-document-node/core";
import { format, isAfter, isBefore, isEqual, parseISO } from "date-fns";
import { uniqBy } from "lodash";
import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "urql";

import { FullPageSpinner } from "@/components/Spinner";
import { TournamentCreateDialog } from "@/components/dialogs/TournamentCreateDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible } from "@/components/ui/collapsible";
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "@/components/ui/pagination";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { FragmentType, getFragmentData, graphql } from "@/gen";

const useTournaments_Tournament = graphql(`
    fragment useTournaments_Tournament on Tournament {
        id
        ...TournamentTable_Tournament
    }
`);

const TOURNAMENTS_PAGE_SIZE = 50;

const useTournaments = ({ currentPage }: { currentPage: number }) => {
    const [tournaments, setTournaments] = useState<ResultOf<typeof useTournaments_Tournament>[]>(
        []
    );

    const offset = currentPage * TOURNAMENTS_PAGE_SIZE;

    const [result, refetch] = useQuery({
        query: graphql(`
            query tournamentsQuery($input: TournamentsInput!) {
                tournaments(input: $input) {
                    items {
                        id
                        ...useTournaments_Tournament
                    }
                    hasMore
                }
            }
        `),
        variables: {
            input: {
                limit: 50,
                offset,
            },
        },
    });

    useEffect(() => {
        if (result.data?.tournaments?.items) {
            const tournaments = getFragmentData(
                useTournaments_Tournament,
                result.data.tournaments.items
            );
            setTournaments(uniqBy(tournaments ?? [], "id"));
        }
    }, [result.data?.tournaments.items]);

    return {
        tournaments,
        hasMore: result.data?.tournaments?.hasMore ?? false,
        loading: result.fetching,
        error: result.error,
        refetch,
    };
};

const useTournamentsParams = () => {
    const [params, setParams] = useSearchParams();

    const updateParam = useCallback(
        (key: string, value?: string, replace?: boolean) => {
            if (value) {
                params.set(key, value);
            } else {
                params.delete(key);
            }
            setParams(params, { replace });
        },
        [params, setParams]
    );

    return {
        pageNumber: parseInt(params.get("page") ?? "0", 10),
        setPageNumber: useCallback(
            (page: number) => updateParam("page", page.toString(), true),
            [updateParam]
        ),
    };
};

const TournamentsRoute = () => {
    const params = useTournamentsParams();

    const { tournaments, hasMore, loading, error } = useTournaments({
        currentPage: params.pageNumber,
    });

    if (loading) {
        return <FullPageSpinner />;
    }

    if (error) {
        return <div className="mb-4 w-full space-y-4 py-4">Error {error.message}</div>;
    }

    return (
        <>
            <Outlet />
            <Tabs defaultValue="all" className="m-4">
                <TabsContent value="all">
                    <TournamentsTable
                        tournamentFragments={tournaments}
                        loading={loading}
                        hasMore={hasMore}
                    />
                </TabsContent>
            </Tabs>
        </>
    );
};

export default TournamentsRoute;

const TournamentTable_Tournament = graphql(`
    fragment TournamentTable_Tournament on Tournament {
        id
        title
        shortTitle
        location
        startAt
        endAt
    }
`);

const isTournamentOngoing = (tournament: {
    startAt: string | Date;
    endAt: string | Date;
}): boolean => {
    const now = new Date();
    const start =
        typeof tournament.startAt === "string" ? parseISO(tournament.startAt) : tournament.startAt;
    const end =
        typeof tournament.endAt === "string" ? parseISO(tournament.endAt) : tournament.endAt;

    return (
        (isAfter(now, start) || isEqual(now, start)) && (isBefore(now, end) || isEqual(now, end))
    );
};

export function TournamentsTable({
    tournamentFragments,
    loading,
    hasMore,
}: {
    tournamentFragments: FragmentType<typeof TournamentTable_Tournament>[];
    loading: boolean;
    hasMore: boolean;
}) {
    const tournaments = getFragmentData(TournamentTable_Tournament, tournamentFragments);
    const navigate = useNavigate();
    const params = useTournamentsParams();

    const tournamentsListRef = useRef<HTMLTableSectionElement>(null);

    const scrollToTop = (ref: RefObject<HTMLTableSectionElement>) => {
        ref.current?.scroll({
            top: 0,
        });
    };

    const prevButtonDisabled = loading || params.pageNumber === 0;
    const nextButtonDisabled = loading || !hasMore;

    return (
        <Card className="bg-brand-white text-brand-black rounded border-none">
            <CardHeader>
                <CardTitle>Tournaments</CardTitle>
            </CardHeader>
            <CardContent>
                <TournamentCreateDialog />
                <Table>
                    <TableHeader>
                        <TableRow className="hover:bg-brand-gray-light">
                            <TableHead className="text-brand-gray-dark">Title</TableHead>
                            <TableHead className="text-brand-gray-dark">Short title</TableHead>
                            <TableHead className="text-brand-gray-dark">Location</TableHead>
                            <TableHead className="text-brand-gray-dark">Start date</TableHead>
                            <TableHead className="text-brand-gray-dark">End date</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody ref={tournamentsListRef}>
                        {tournaments.map(tournament => {
                            const isOngoing = isTournamentOngoing(tournament);

                            return (
                                <Collapsible key={tournament.id} asChild>
                                    <>
                                        <TableRow
                                            className={`hover:bg-brand-purple cursor-pointer hover:bg-opacity-20 ${
                                                isOngoing ? "bg-brand-yellow/40" : ""
                                            }`}
                                            key={tournament.id}
                                            onClick={() =>
                                                navigate(`/tournaments/${tournament.id}`)
                                            }
                                        >
                                            <TableCell className="font-medium">
                                                {tournament.title}
                                            </TableCell>
                                            <TableCell className="font-medium">
                                                {tournament.shortTitle}
                                            </TableCell>
                                            <TableCell className="font-medium">
                                                {tournament.location}
                                            </TableCell>
                                            <TableCell className="font-medium">
                                                {format(tournament.startAt, "P p")}
                                            </TableCell>
                                            <TableCell className="font-medium">
                                                {tournament.endAt
                                                    ? format(tournament.endAt, "P p")
                                                    : "N/A"}
                                            </TableCell>
                                        </TableRow>
                                    </>
                                </Collapsible>
                            );
                        })}
                    </TableBody>
                </Table>
            </CardContent>
            <Pagination className="my-4">
                <PaginationContent>
                    {!prevButtonDisabled && (
                        <>
                            <PaginationItem
                                onClick={() => {
                                    scrollToTop(tournamentsListRef);
                                    params.setPageNumber(Math.max(0, params.pageNumber - 1));
                                }}
                            >
                                <PaginationPrevious />
                            </PaginationItem>

                            <PaginationItem
                                onClick={() => {
                                    scrollToTop(tournamentsListRef);
                                    params.setPageNumber(Math.max(0, params.pageNumber - 1));
                                }}
                            >
                                <PaginationLink>{params.pageNumber}</PaginationLink>
                            </PaginationItem>
                        </>
                    )}
                    <PaginationItem>
                        <PaginationLink href="#" isActive>
                            {params.pageNumber + 1}
                        </PaginationLink>
                    </PaginationItem>
                    <PaginationItem
                        onClick={() => {
                            scrollToTop(tournamentsListRef);
                            params.setPageNumber(params.pageNumber + 1);
                        }}
                    >
                        {!nextButtonDisabled && (
                            <PaginationLink href="#">{params.pageNumber + 2}</PaginationLink>
                        )}
                    </PaginationItem>
                    {!nextButtonDisabled && (
                        <>
                            <PaginationItem>
                                <PaginationEllipsis />
                            </PaginationItem>
                            <PaginationItem
                                onClick={() => {
                                    scrollToTop(tournamentsListRef);
                                    params.setPageNumber(params.pageNumber + 1);
                                }}
                            >
                                <PaginationNext />
                            </PaginationItem>
                        </>
                    )}
                </PaginationContent>
            </Pagination>
        </Card>
    );
}
